
<script setup>
import { computed, onBeforeMount, ref } from 'vue'
import InputText from '@/components/Forms/InputText.vue'
import Combobox from '@/components/Forms/Combobox.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import { useAuthStore } from '@/stores/authStore.js'
import { organizationsApi } from '@/api/Organizations/OrganizationsApi.js'
import { userOrganizationsApi } from '@/api/Organizations/UserOrganizationsApi.js'
import { useOrganizationStore } from '@/stores/organizationStore.js'
import { OrganizationModalStep } from '@/models/enums/OrganizationModalStep.js'
import { FormData } from '@/models/enums/FormData.js'
import { OrganizationModalData } from '@/models/enums/OrganizationModalData.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { userApi } from '@/api/User/UserApi.js'

const authStore = useAuthStore()
const orgStore = useOrganizationStore()

const auth = authStore.getAuth
const isLoading = ref(false)
const error = ref(null)

const organization_name = ref('')
const organization_address = ref('')
const city = ref("")
const states = FormData.STATES
const selected_state = ref('')
const zip = ref('')
const ccn = ref('')
const search_results = ref(null)
const selected_organization = ref(null)
const showNoResultsFound = ref(false)
const noResultsMessage = ref('Please enter search criteria.')

const npiTooltip = OrganizationModalData.NPI_TOOLTIP + '<br><br>' + OrganizationModalData.TIN_TOOLTIP + '<br><br>' + OrganizationModalData.CCN_TOOLTIP

const emit = defineEmits(['onShowContactUs'])

defineProps({
	show: { type: Boolean, default: false },
})

onBeforeMount(()=>{
	//Allow users to clear the State selection in adv search
	if (states[0] !== "") states.unshift("")
})

/**
 * Returns true if the org is being added to a different user than the one logged in
 * @vue-computed {boolean} True if another user
 * @author AJL (2025-10-15)
 */
const addingForOtherUser = computed(() => {
	return orgStore.getTargetUserId > 0;
})

/**
 * Gets the id of the user this is being added for. If the target User ID is not set, then the currently logged-in user is used
 * @returns {number} User ID that org is being added to. Either id of user from Admin user list, or user's menu
 * @author AJL (2025-10-15)
 */
function getTargetUserId()
{
	return (addingForOtherUser.value) ? orgStore.getTargetUserId : auth?.user()?.id
}

/**
 * Gets the existing organizations for the user an org is being added to
 * @returns {Array<number>}
 * @author AJL (2025-10-15)
 */
function getExisingOrgKeys()
{
	let exisingOrgKeys
	if (addingForOtherUser.value)
	{
		exisingOrgKeys = orgStore.getExistingOrgKeys
	}
	else
	{
		exisingOrgKeys = (authStore.userOrganizations.length > 0) ?
			authStore.userOrganizations.map((org) => org.id) : []
	}
	return exisingOrgKeys
}

/**
 * Shows or hides the Advanced Search section of the modal. Clears fields when selected.
 */
function toggleAdvancedSearch()
{
	ccn.value = ''
	organization_address.value = ''
	city.value = ''
	selected_state.value = ''
	zip.value = ''
	orgStore.toggleAdvancedSearch()
}

/**
 * Initiates search for organizations
 */
function findProvider()
{
	error.value = null
	isLoading.value = true

	const existingOrgs = getExisingOrgKeys()//(authStore.userOrganizations.length > 0) ? authStore.userOrganizations.map((org) => org.id) : []

	const searchCriteria = {
		'ccn': ccn.value,
		'name': organization_name.value,
		'address': organization_address.value,
		'city': city.value,
		'state': selected_state.value,
		'zip': zip.value,
	}

	//Make sure something is entered
	if (!checkCriteria(searchCriteria))
	{
		noResultsMessage.value = 'Please enter search criteria.'
		showNoResultsFound.value = true
		isLoading.value = false
		return
	}

	organizationsApi.search(
		searchCriteria,
		'organization_name',
		10,
		existingOrgs,
		(response) =>
		{
			isLoading.value = false
			selected_organization.value = null

			if (response.length > 0)
			{
				search_results.value = response
				selected_organization.value = search_results.value[0]
				orgStore.setCurrentStep(OrganizationModalStep.SEARCH_RESULTS)
			}
			else if (error.value === null)
			{
				noResultsMessage.value = 'No unassigned organizations found. Please try again.'
				showNoResultsFound.value = true
				orgStore.setCurrentStep(OrganizationModalStep.SEARCH)
			}
		}, (e) =>
		{
			error.value = e.showErrorBanner()
			isLoading.value = false

		})
}

function checkCriteria(p_criteria)
{
	for (const key in p_criteria)
	{
		if (p_criteria[key].length > 0)
		{
			return true;
		}
	}

	return false;
}

/**
 * Initiates request to add organization to user_organizations
 */
function confirmProvider()
{
	error.value = null
	isLoading.value = true

	if (selected_organization.value)
	{
		//Check if we're updating our own orgs
		const updateUserOrgs = getTargetUserId() === 0 || getTargetUserId() === auth?.user()?.id

		//Create user_organizations record
		userOrganizationsApi.create(auth?.user(), getTargetUserId(), selected_organization.value.id,
			() =>
			{
				//Only update the logged-in user's orgs if they modified theirs
				if (updateUserOrgs)
				{
					userApi.refreshUserData()
					isLoading.value = false
					orgStore.setCurrentStep(OrganizationModalStep.ADDED_ORG_TO_USER)
				}
				else
				{
					isLoading.value = false
					orgStore.setCurrentStep(OrganizationModalStep.ADDED_ORG_TO_USER)
				}

			}, (e) =>
			{
				error.value = e.showErrorBanner()
				isLoading.value = false
				orgStore.setCurrentStep(OrganizationModalStep.SEARCH)
			}
		)
	}
}

/**
 * Returns user to the search page
 */
function returnToSearch()
{
	orgStore.setCurrentStep(OrganizationModalStep.SEARCH)
}

/**
 * Hide the modal
 */
function dismissModal()
{
	organization_name.value = ''
	organization_address.value = ''
	city.value = ''
	selected_state.value = ''
	zip.value = ''
	ccn.value = ''
	search_results.value = null
	selected_organization.value = null
	showNoResultsFound.value = false

	orgStore.toggleAddModal(false);
}

function hideNoResults()
{
	showNoResultsFound.value = false
}

function logout()
{
	authStore.logout()
}

function showContactUs()
{
	emit('onShowContactUs')
}
</script>

<template>
	<BaseModal :show="show">
		<div class="bg-neutral w-125 ">
			<div v-if="orgStore.getCurrentStep === OrganizationModalStep.START" class="flex flex-col items-center">
				<p class="text-2xl font-bold text-heading leading-[30px]">Let's get started.</p>
				<div class="flex mt-9 cursor-pointer" @click.prevent="orgStore.setCurrentStep(OrganizationModalStep.SEARCH)">
					<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" class="mr-3">
						<title>Add</title>
						<path d="M21.0998 14.8508H17.0998V10.9008C17.0998 10.3008 16.5998 9.80078 15.9498 9.80078C15.3498 9.80078 14.8498 10.3008 14.8498 10.9508V14.9008H10.8998C10.2998 14.9008 9.7998 15.4008 9.7998 16.0508C9.7998 16.6508 10.2998 17.1508 10.9498 17.1508H14.8998V21.1008C14.8998 21.7008 15.3998 22.2008 16.0498 22.2008C16.6498 22.2008 17.1498 21.7008 17.1498 21.0508V17.1008H21.0998C21.6998 17.1008 22.1998 16.6008 22.1998 15.9508C22.1998 15.3508 21.6998 14.8508 21.0998 14.8508Z" class="fill-icon-primary"/>
						<path d="M15.9999 0.900391C7.6499 0.900391 0.899902 7.65039 0.899902 16.0004C0.899902 24.3504 7.6999 31.1504 16.0499 31.1504C24.3999 31.1504 31.1999 24.3504 31.1999 16.0004C31.1499 7.65039 24.3499 0.900391 15.9999 0.900391ZM15.9999 28.9004C8.8999 28.9004 3.1499 23.1004 3.1499 16.0004C3.1499 8.90039 8.8999 3.15039 15.9999 3.15039C23.0999 3.15039 28.8999 8.90039 28.8999 16.0004C28.8999 23.1004 23.0999 28.9004 15.9999 28.9004Z" class="fill-icon-primary"/>
					</svg>
					<p class="text-2xl font-bold leading-[30px] text-primary">Add an Organization</p></div>
			</div>

			<div v-if="orgStore.getCurrentStep === OrganizationModalStep.SEARCH">
				<div class="flex items-center justify-between">
					<p class="flex-grow text-xl font-bold text-heading leading-7">Find organization</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal">
				</div>

				<form>
					<InputText label="Organization name" type="text" placeholder="Enter organization name" input-height="h-10" label-text-size="text-sm"
									v-model="organization_name" class="mt-3" />
					<div class="flex flex-row cursor-pointer text-primary" @click="toggleAdvancedSearch">
						Advanced Search
						<ChevronUpIcon v-if="orgStore.getShowAdvancedSearch" class="size-5 text-icon-primary mt-[2px]" aria-hidden="true" />
						<ChevronDownIcon v-else class="size-5 text-icon-primary mt-[2px]" aria-hidden="true" />
					</div>
					<div v-show="orgStore.getShowAdvancedSearch">
						<!-- NPI -->
						<InputText label="NPI, TIN or CMS Certification number" type="text" placeholder="Enter NPI, TIN or CCN" v-model="ccn" label-text-size="text-sm"
										class="mt-3" input-height="h-10" :tooltip="npiTooltip"
										tooltip-location="bottom" />

						<!-- Address -->
						<InputText label="Organization address" type="text" placeholder="Enter organization physical address" input-height="h-10"
										label-text-size="text-sm"
										v-model="organization_address" class="mt-3" />

						<!-- CITY -->
						<InputText label="City" type="text" placeholder="Enter city" v-model="city" class="mt-3" label-text-size="text-sm"
										input-height="h-10" />

						<div class="flex flex-row gap-x-4">
							<!-- STATE -->
							<Combobox id="combo_add_org_state" v-model="selected_state" :items="states" title="State" placeholder="Select state"
										 label-text-size="text-sm"
										 :show-options-on-top="true" input-height="h-10" :readonly="false" />

							<!-- ZIP CODE -->
							<InputText label="Zip code" type="number" placeholder="Enter 5 digit code" :max-length=5 input-height="h-10"
											label-text-size="text-sm" v-model="zip" />
						</div>
					</div>

					<SubmitButton class="w-full mt-6" label="Find Organization" :loading="isLoading" @click="findProvider" />
				</form>
			</div>

			<div v-if="orgStore.getCurrentStep === OrganizationModalStep.SEARCH_RESULTS">
				<div class="flex items-center justify-between">
					<p class="flex-grow text-xl font-bold text-heading leading-7">{{ search_results.length > 1 ? 'Select' : 'Confirm' }}{{ addingForOtherUser ? '' : ' your'}} organization</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal">
				</div>

				<!-- Search Results -->
				<fieldset class="max-h-70 overflow-y-scroll mt-8">
					<div v-for="(result, index) in search_results" :key="result.id" class="relative flex items-start cursor-pointer p-2 hover:bg-primary-subtlest"
							:class="{ 'border-b border-neutral' : index !== search_results.length-1 }"
							@click="selected_organization = result">
						<div class="flex h-6 items-center">
							<label class="container">
								<input type="radio"
										 :checked="(selected_organization && result.id === selected_organization.id) || (!selected_organization && index === 0)"
										 @change.prevent="selected_organization = result" :name="result.id">
								<span class="checkmark"></span>
							</label>
						</div>
						<div class="ml-3 text-sm/6">
							<p class="text-label">{{ result.organization_name + (result.organization_name_dba ? " (" + result.organization_name_dba + ")" : "") }}</p>
							<p class="text-label">CCN: {{ result.ccn }}</p>
							<p :id="`${result.id}-name`" class="text-label">{{ result.street }}</p>
							<p :id="`${result.id}-address`" class="text-label">{{ `${result.city}, ${result.state} ${result.zip}` }}</p>
							<p v-if="result.facility_type != null" :id="`${result.id}-specialty`"
								class="font-semibold text-label">{{ `Specialty: ${result.facility_type}` }}</p>
						</div>
					</div>
				</fieldset>

				<div class="flex flex-col sm:flex-row gap-x-4 items-center mt-6">
					<SubmitButton label="Cancel" class="ml-auto" :disabled="isLoading" type="outline" @click.prevent="returnToSearch" />
					<SubmitButton label="Confirm" :loading="isLoading" :disabled="isLoading" @click.prevent="confirmProvider" />
				</div>

				<ValidationError :errors="error" :field="null" />
			</div>

			<div v-if="orgStore.getCurrentStep === OrganizationModalStep.ADDED_ORG_TO_USER" class="flex flex-col items-center">
				<p class="flex-grow text-xl font-bold text-heading leading-7">Organization Added</p>
				<div class="w-[90px] h-[3px] bg-primary rounded-[2px] my-6" />
				<p v-if="addingForOtherUser" class="text-grayscale-body text-center font-medium leading-6">The selected organization has been successfully added.</p>
				<p v-if="!addingForOtherUser" class="text-grayscale-body text-center font-medium leading-6">The selected organization has been successfully added to your organizations.</p>
				<SubmitButton class="w-48 mt-6" label="OK" :loading="isLoading" @click="dismissModal" />
			</div>

			<div v-if="orgStore.getCurrentStep === OrganizationModalStep.NO_ACTIVE_ORGS" class="flex flex-col items-center">
				<p class="flex-grow text-xl font-bold text-heading leading-7">No Active Organizations</p>
				<div class="w-[90px] h-[3px] bg-primary rounded-[2px] my-6" />
				<p class="text-grayscale-body text-center font-medium leading-6">Your account has no associated active Organizations. If you believe this is in error please contact your Portal point of contact or use the contact us link below.</p>
				<p class="text-center text-grayscale-body mt-6 leading-6"><span class="clickable-text" @click="showContactUs">Contact us</span></p>
				<SubmitButton class="mt-8 mx-10" label="Logout" @click="logout" />

			</div>
		</div>
	</BaseModal>

	<Teleport to="html">
		<MultiButtonModal
			:show="showNoResultsFound"
			icon="alert"
			title="No Organizations Found"
			:message="noResultsMessage"
			:buttons="[
									new ModalButtonData(
										'Ok', hideNoResults, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>
</template>

<style scoped>

</style>